import {
  type InboxSettings,
  type InboxSettingsInput,
  inboxSettingsInput,
  type ReceptionSummaryRow,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  CheckCircle2Icon,
  FileWarningIcon,
  HardHatIcon,
  InboxIcon,
  ListChecksIcon,
  MailIcon,
  RefreshCwIcon,
  UploadCloudIcon,
  XIcon,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import {
  ClearFilters,
  FilterBar,
  FilterDateRange,
  FilterSearch,
  FilterSelect,
  FilterTabs,
} from '@/components/ui/filter-bar'
import { Input } from '@/components/ui/input'
import { Checkbox, Switch } from '@/components/ui/misc'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { HaciendaBadge } from '@/features/documents/status'
import { useProjectsAccess } from '@/features/projects/access'
import { fileToBase64, inboxQuery, receptionsQuery } from '@/features/receptions/api'
import { BulkDecisionDialog } from '@/features/receptions/bulk-decision-dialog'
import { DecisionBadge, ReceptionSheet } from '@/features/receptions/reception-sheet'
import { api, errorMessage } from '@/lib/api'
import { formatDate, formatIdentification, formatMoney, formatRelative } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(['pending', 'accepted', 'partial', 'rejected', 'error']).optional(),
  allocation: z.enum(['unassigned']).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().min(1).optional(),
  id: z.string().optional(),
})

export const Route = createFileRoute('/_app/receptions')({
  staticData: { crumb: 'Recepción' },
  validateSearch: searchSchema,
  component: ReceptionsPage,
})

const col = columnHelper<ReceptionSummaryRow>()

function ReceptionsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canManage = useCan('receptions:manage')
  const projectsAccess = useProjectsAccess()
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [inboxOpen, setInboxOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const list = useQuery(
    receptionsQuery({
      search: search.q,
      status: search.status,
      allocation: projectsAccess.read ? search.allocation : undefined,
      from: search.from,
      to: search.to,
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )
  const pendingOnPage = useMemo(
    () => (list.data?.items ?? []).filter((r) => r.decision === null).map((r) => r.id),
    [list.data],
  )
  const allSelected = pendingOnPage.length > 0 && pendingOnPage.every((id) => selected.has(id))
  const toggle = useCallback(
    (id: string, on: boolean) =>
      setSelected((prev) => {
        const next = new Set(prev)
        if (on) next.add(id)
        else next.delete(id)
        return next
      }),
    [],
  )
  const pending = useQuery(receptionsQuery({ status: 'pending', pageSize: 1 }))

  const columns = useMemo(
    () => [
      ...(canManage
        ? [
            col.display({
              id: 'select',
              meta: { className: 'w-10 !pr-0' },
              header: () => (
                <Checkbox
                  aria-label="Elegir todos los pendientes de la página"
                  checked={allSelected ? true : selected.size > 0 ? 'indeterminate' : false}
                  disabled={pendingOnPage.length === 0}
                  onCheckedChange={(on) =>
                    setSelected((prev) => {
                      const next = new Set(prev)
                      for (const id of pendingOnPage) {
                        if (on === true) next.add(id)
                        else next.delete(id)
                      }
                      return next
                    })
                  }
                />
              ),
              cell: ({ row }) =>
                row.original.decision === null ? (
                  // biome-ignore lint/a11y/noStaticElementInteractions: evita abrir la hoja al marcar la casilla
                  // biome-ignore lint/a11y/useKeyWithClickEvents: la casilla ya es operable con teclado
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      aria-label={`Elegir ${row.original.issuerName}`}
                      checked={selected.has(row.original.id)}
                      onCheckedChange={(on) => toggle(row.original.id, on === true)}
                    />
                  </div>
                ) : null,
            }),
          ]
        : []),
      col.accessor('issuerName', {
        header: 'Proveedor',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{row.original.issuerName}</span>
              {row.original.source === 'email' && (
                <MailIcon className="size-3.5 shrink-0 text-muted-foreground" />
              )}
            </div>
            <div className="font-mono text-muted-foreground text-xs">
              {formatIdentification(
                row.original.issuerIdentification.length === 9 ? '01' : '02',
                row.original.issuerIdentification,
              )}
            </div>
          </div>
        ),
      }),
      col.accessor('documentKind', {
        header: 'Documento',
        meta: { hideOnMobile: true },
        cell: ({ row }) => (
          <div>
            <div className="flex items-center gap-1.5">
              <Badge variant="outline">{row.original.documentKind}</Badge>
              <span className="font-mono text-muted-foreground text-xs">
                {row.original.consecutive.slice(-10)}
              </span>
            </div>
            <div className="mt-0.5 text-muted-foreground text-xs">{formatDate(row.original.issuedAt)}</div>
          </div>
        ),
      }),
      col.accessor('decision', {
        header: 'Respuesta',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <DecisionBadge decision={row.original.decision} />
            {row.original.decision && row.original.haciendaStatus !== 'accepted' && (
              <HaciendaBadge status={row.original.haciendaStatus} />
            )}
            <ProjectAllocationBadge row={row.original} />
          </div>
        ),
      }),
      col.accessor('taxTotal', {
        header: 'IVA',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatMoney(row.original.taxTotal, row.original.currency as 'CRC')}
          </span>
        ),
      }),
      col.accessor('total', {
        header: 'Total',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="font-medium">
            {formatMoney(row.original.total, row.original.currency as 'CRC')}
          </span>
        ),
      }),
    ],
    [canManage, selected, allSelected, pendingOnPage, toggle],
  )

  return (
    <Page>
      <PageHeader
        title="Recepción de comprobantes"
        description="Aceptá o rechazá ante Hacienda las facturas de tus proveedores. Lo aceptado se registra como gasto."
        actions={
          canManage && (
            <>
              <Button variant="outline" onClick={() => setInboxOpen(true)}>
                <InboxIcon /> Buzón de correo
              </Button>
              <Button onClick={() => setUploadOpen(true)}>
                <UploadCloudIcon /> Subir XML
              </Button>
            </>
          )
        }
      />
      {(pending.data?.total ?? 0) > 0 && !search.status && (
        <button
          type="button"
          onClick={() => setSearch({ status: 'pending' })}
          className="mb-5 flex w-full items-center gap-3 rounded-xl border border-info/25 bg-info-soft px-4 py-3 text-left text-info-soft-foreground text-sm transition hover:brightness-105"
        >
          <FileWarningIcon className="size-5" />
          <span className="flex-1">
            Tenés <strong>{pending.data?.total}</strong> comprobante{pending.data?.total === 1 ? '' : 's'}{' '}
            esperando respuesta. Hacienda da 8 días hábiles para aceptarlos o rechazarlos.
          </span>
          <span className="font-medium">Revisar</span>
        </button>
      )}
      {selected.size > 0 && (
        <div className="sticky top-2 z-20 mb-4 flex items-center gap-3 rounded-xl border border-primary/25 bg-card px-4 py-2.5 shadow-elevation-2">
          <ListChecksIcon className="size-5 text-primary" />
          <span className="flex-1 text-sm">
            <strong>{selected.size}</strong> comprobante{selected.size === 1 ? '' : 's'} elegido
            {selected.size === 1 ? '' : 's'}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            <XIcon /> Quitar selección
          </Button>
          <Button size="sm" onClick={() => setBulkOpen(true)}>
            Procesar múltiples
          </Button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(r) => r.id}
        onRowClick={(r) => setSearch({ id: r.id })}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Estado"
              value={search.status ?? 'all'}
              onValueChange={(v) =>
                setSearch({ status: v === 'all' ? undefined : (v as 'pending'), page: undefined })
              }
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'pending', label: 'Pendientes' },
                { value: 'accepted', label: 'Aceptados' },
                { value: 'partial', label: 'Parciales' },
                { value: 'rejected', label: 'Rechazados' },
                { value: 'error', label: 'Con error' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por proveedor o clave…" />
            {projectsAccess.read && (
              <FilterSelect
                label="Proyecto"
                value={search.allocation ?? 'all'}
                onValueChange={(v) =>
                  setSearch({ allocation: v === 'all' ? undefined : 'unassigned', page: undefined })
                }
                options={[
                  { value: 'all', label: 'Con y sin proyecto' },
                  { value: 'unassigned', label: 'Sin proyecto' },
                ]}
              />
            )}
            <FilterDateRange
              value={{ from: search.from, to: search.to }}
              onChange={(r) => setSearch({ from: r.from, to: r.to, page: undefined })}
            />
            <ClearFilters
              active={!!(search.q || search.status || search.allocation || search.from || search.to)}
              onClear={() => {
                setText('')
                setSearch({
                  q: undefined,
                  status: undefined,
                  allocation: undefined,
                  from: undefined,
                  to: undefined,
                  page: undefined,
                })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={InboxIcon}
            title="Sin comprobantes recibidos"
            description="Subí los XML de tus proveedores o conectá un buzón de correo para importarlos solos."
            action={
              canManage && (
                <Button onClick={() => setUploadOpen(true)}>
                  <UploadCloudIcon /> Subir XML
                </Button>
              )
            }
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <ReceptionSheet id={search.id} onClose={() => setSearch({ id: undefined })} />
      <BulkDecisionDialog
        open={bulkOpen}
        ids={[...selected]}
        onClose={() => setBulkOpen(false)}
        onDone={(done) =>
          setSelected((prev) => {
            const next = new Set(prev)
            for (const id of done) next.delete(id)
            return next
          })
        }
      />
      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <InboxDialog open={inboxOpen} onClose={() => setInboxOpen(false)} />
    </Page>
  )
}

interface UploadResult {
  name: string
  ok: boolean
  message: string
}

function UploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [results, setResults] = useState<UploadResult[]>([])
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (open) {
      setFiles([])
      setResults([])
    }
  }, [open])

  async function upload() {
    setBusy(true)
    const out: UploadResult[] = []
    const pdfs = files.filter((f) => /\.pdf$/i.test(f.name))
    for (const file of files.filter((f) => /\.xml$/i.test(f.name))) {
      const base = file.name.replace(/\.xml$/i, '')
      const pdf = pdfs.find((p) => p.name.replace(/\.pdf$/i, '') === base)
      try {
        const r = await api.post<{ issuerName: string }>('/receptions', {
          xmlBase64: await fileToBase64(file),
          pdfBase64: pdf ? await fileToBase64(pdf) : null,
        })
        out.push({ name: file.name, ok: true, message: r.issuerName })
      } catch (error) {
        out.push({ name: file.name, ok: false, message: errorMessage(error) })
      }
      setResults([...out])
    }
    setBusy(false)
    queryClient.invalidateQueries({ queryKey: ['receptions'] })
    const ok = out.filter((r) => r.ok).length
    if (ok) toast.success(`${ok} comprobante${ok === 1 ? '' : 's'} recibido${ok === 1 ? '' : 's'}`)
  }

  const xmlCount = files.filter((f) => /\.xml$/i.test(f.name)).length

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Subir comprobantes de proveedores</DialogTitle>
          <DialogDescription>
            Podés soltar varios XML a la vez; si traen su PDF con el mismo nombre, se adjunta.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-4">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              setFiles([...files, ...Array.from(e.dataTransfer.files)])
            }}
            className={cn(
              'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 text-center transition',
              dragging
                ? 'border-primary bg-primary-soft/50'
                : 'border-border hover:border-foreground/25 hover:bg-accent/40',
            )}
          >
            <UploadCloudIcon className="size-8 text-muted-foreground" />
            <span className="font-medium text-sm">
              {files.length
                ? `${xmlCount} XML · ${files.length - xmlCount} PDF`
                : 'Arrastrá los archivos aquí'}
            </span>
            <span className="text-muted-foreground text-xs">o hacé clic para elegirlos</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".xml,.pdf,text/xml,application/pdf"
            className="hidden"
            onChange={(e) => setFiles([...files, ...Array.from(e.target.files ?? [])])}
          />
          {results.length > 0 && (
            <div className="grid max-h-56 gap-1.5 overflow-y-auto">
              {results.map((r) => (
                <div key={r.name} className="flex items-start gap-2 text-sm">
                  {r.ok ? (
                    <CheckCircle2Icon className="mt-0.5 size-4 text-success" />
                  ) : (
                    <FileWarningIcon className="mt-0.5 size-4 text-destructive" />
                  )}
                  <div className="min-w-0">
                    <div className="truncate font-medium">{r.name}</div>
                    <div className={r.ok ? 'text-muted-foreground text-xs' : 'text-destructive text-xs'}>
                      {r.message}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cerrar
          </Button>
          <Button onClick={upload} loading={busy} disabled={!xmlCount}>
            Recibir {xmlCount > 0 && xmlCount}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function InboxDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const inbox = useQuery({ ...inboxQuery, enabled: open })
  const form = useForm<InboxSettingsInput>({
    resolver: zodResolver(inboxSettingsInput),
    values: inbox.data
      ? {
          enabled: inbox.data.configured ? inbox.data.enabled : true,
          host: inbox.data.host ?? '',
          port: inbox.data.port,
          secure: inbox.data.secure,
          username: inbox.data.username ?? '',
          password: '',
          folder: inbox.data.folder,
        }
      : undefined,
  })
  const save = useMutation({
    mutationFn: (v: InboxSettingsInput) => api.put<InboxSettings>('/inbox', v),
    onSuccess: (data) => {
      queryClient.setQueryData(inboxQuery.queryKey, data)
      toast.success('Buzón guardado')
    },
  })
  const sync = useMutation({
    mutationFn: () => api.post<{ imported: number; skipped: number }>('/inbox/sync'),
    onSuccess: (r) => {
      toast.success(`${r.imported} importados · ${r.skipped} omitidos`)
      queryClient.invalidateQueries({ queryKey: ['receptions'] })
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const { errors } = form.formState

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
          <DialogHeader>
            <DialogTitle>Buzón de recepción</DialogTitle>
            <DialogDescription>
              Facture revisa este correo cada 3 minutos e importa los XML que te envían tus proveedores.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            {inbox.data?.lastSyncAt && (
              <div
                className={cn(
                  'rounded-lg px-3 py-2 text-sm',
                  inbox.data.lastError
                    ? 'bg-destructive-soft text-destructive-soft-foreground'
                    : 'bg-success-soft text-success-soft-foreground',
                )}
              >
                {inbox.data.lastError ??
                  `Última revisión ${formatRelative(inbox.data.lastSyncAt)} · ${inbox.data.imported} importados en total`}
              </div>
            )}
            <div className="grid grid-cols-[1fr_6rem] gap-3">
              <Field label="Servidor IMAP" error={errors.host} required>
                {(c) => <Input {...c} {...form.register('host')} placeholder="imap.gmail.com" />}
              </Field>
              <Field label="Puerto" error={errors.port}>
                {(c) => <Input {...c} {...form.register('port')} type="number" />}
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Usuario" error={errors.username} required>
                {(c) => <Input {...c} {...form.register('username')} autoComplete="off" />}
              </Field>
              <Field
                label="Contraseña"
                hint={inbox.data?.hasPassword ? 'Vacía = conservar' : 'Usá una contraseña de aplicación'}
              >
                {(c) => (
                  <Input {...c} {...form.register('password')} type="password" autoComplete="new-password" />
                )}
              </Field>
            </div>
            <Field label="Carpeta" error={errors.folder}>
              {(c) => <Input {...c} {...form.register('folder')} className="max-w-56" />}
            </Field>
            <div className="flex gap-6">
              <Controller
                control={form.control}
                name="secure"
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={!!field.value} onCheckedChange={field.onChange} /> SSL/TLS
                  </label>
                )}
              />
              <Controller
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={!!field.value} onCheckedChange={field.onChange} /> Revisar
                    automáticamente
                  </label>
                )}
              />
            </div>
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              disabled={!inbox.data?.configured}
              loading={sync.isPending}
              onClick={() => sync.mutate()}
            >
              <RefreshCwIcon /> Revisar ahora
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cerrar
              </Button>
              <Button type="submit" loading={save.isPending}>
                Guardar
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Módulo Proyectos: aceptados sin imputar (o en parte) y, si está imputado, a qué proyecto. */
function ProjectAllocationBadge({ row }: { row: ReceptionSummaryRow }) {
  if (!row.allocation) return null
  if (row.allocation === 'none')
    return (
      <Badge variant="warning">
        <HardHatIcon /> Sin proyecto
      </Badge>
    )
  return (
    <Badge
      variant={row.allocation === 'full' ? 'neutral' : 'warning'}
      className="max-w-48"
      title={row.projectNames ?? ''}
    >
      <HardHatIcon />
      <span className="truncate">
        {row.allocation === 'partial' ? 'En parte · ' : ''}
        {row.projectNames}
      </span>
    </Badge>
  )
}
